import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Scale,
  ShieldCheck,
  Users,
  Wrench,
  FileText,
  Bell,
  LayoutDashboard,
  History,
  BarChart2,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import ComplianceDashboard from './ComplianceDashboard';
import AssetRegister from './AssetRegister';
import CalibrationTracker from './CalibrationTracker';
import { OperatorManagement } from './OperatorManagement';
import { MaintenanceSchedule } from './MaintenanceSchedule';
import { DocumentManager as DocumentLibrary } from './DocumentManager';
import { NotificationConfig } from './NotificationConfig';
import { AuditLog } from './AuditLog';
import { NCRRegister } from './NCRRegister';
import ComplianceTrends from './ComplianceTrends';
import ComplianceReports from './ComplianceReports';
import PageHeader from '../../common/PageHeader';

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'assets', label: 'Assets', icon: Scale },
  { id: 'calibrations', label: 'Calibrations', icon: ShieldCheck },
  { id: 'operators', label: 'Operators', icon: Users },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'ncr', label: 'NCR', icon: AlertTriangle },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'trends', label: 'Trends', icon: TrendingUp },
  { id: 'reports', label: 'Reports', icon: BarChart2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'audit', label: 'Audit Log', icon: History },
];

const tabComponents = {
  dashboard: ComplianceDashboard,
  assets: AssetRegister,
  calibrations: CalibrationTracker,
  operators: OperatorManagement,
  maintenance: MaintenanceSchedule,
  ncr: NCRRegister,
  documents: DocumentLibrary,
  trends: ComplianceTrends,
  reports: ComplianceReports,
  notifications: NotificationConfig,
  audit: AuditLog,
};

const WeighbridgeCompliance = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const activeRef = useRef(null);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const tab = activeRef.current;
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      const viewLeft = container.scrollLeft;
      const viewRight = viewLeft + container.clientWidth;

      if (tabLeft < viewLeft + 32) {
        container.scrollTo({ left: tabLeft - 32, behavior: 'smooth' });
      } else if (tabRight > viewRight - 32) {
        container.scrollTo({ left: tabRight - container.clientWidth + 32, behavior: 'smooth' });
      }
    }
  }, [activeTab]);

  const ActiveComponent = tabComponents[activeTab];

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={Scale}
        title="Weighbridge Compliance"
        subtitle="Assets, calibrations, maintenance, and regulatory compliance"
      />

      <div className="space-y-5 p-3 sm:p-6 lg:p-8 max-w-[1800px] mx-auto">
        {/* Tab navigation */}
        <div className="relative">
          {/* Scroll fade indicators */}
          {canScrollLeft && (
            <div
              className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none rounded-l-xl"
              style={{ background: 'linear-gradient(to right, white 30%, transparent)' }}
            />
          )}
          {canScrollRight && (
            <div
              className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none rounded-r-xl"
              style={{ background: 'linear-gradient(to left, white 30%, transparent)' }}
            />
          )}

          <div
            ref={scrollRef}
            className="flex items-center gap-0.5 bg-surface rounded-xl border border-slate-200/80 p-1 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeRef : null}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                  relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                  whitespace-nowrap transition-all duration-200 flex-shrink-0
                  ${
                    isActive
                      ? 'bg-primary-500 text-white shadow-sm shadow-primary-500/25'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50/80'
                  }
                `}
                >
                  <Icon className="w-3.5 h-3.5" style={{ width: 14, height: 14 }} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active tab content */}
        <ActiveComponent />
      </div>
    </div>
  );
};

export default WeighbridgeCompliance;
